import React, { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import RecurringWarningModal from '../components/RecurringWarningModal';
import RecurringBlockingModal from '../components/RecurringBlockingModal';
import api from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [recurringStatus, setRecurringStatus] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ clients:0, facilities:0, techCount:0, completedOrders:0, scheduledOrders:0, pendingOrders:0, inProgressOrders:0, completionRate:0, overdueInvoices:0, pendingInvoicesAmt:0 });
  const [upcomingOrders, setUpcomingOrders] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [facilitiesNoService, setFacilitiesNoService] = useState(0);
  const [companyLicenses, setCompanyLicenses] = useState([]);
  const [timingStats, setTimingStats] = useState(null);

  useEffect(() => { checkRecurringStatus(); loadDashboard(); }, []);

  const checkRecurringStatus = async () => {
    try {
      const localDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
      const r = await api.get('/recurring/status?localDate=' + localDate);
      setRecurringStatus(r.data);
      if (r.data.shouldShowWarning && !r.data.mustGenerate) setShowWarning(true);
    } catch (err) { console.error(err); }
  };

  const loadDashboard = async () => {
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      const [clientsRes, facilitiesRes, ordersRes, techsRes, licensesRes, invoicesRes] = await Promise.all([
        api.get('/clients').catch(() => ({ data: { data: [] } })),
        api.get('/facilities').catch(() => ({ data: { data: [] } })),
        api.get('/service-orders').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: { data: [] } })),
        api.get('/licenses/expiring-soon').catch(() => ({ data: { data: [] } })),
        api.get('/invoices').catch(() => ({ data: { data: [] } }))
      ]);

      const clients = clientsRes.data.data || [];
      const facilities = facilitiesRes.data.data || [];
      const orders = ordersRes.data.data || ordersRes.data || [];
      const users = techsRes.data.data || [];
      const techs = users.filter(u => u.is_technician);
      const licenses = licensesRes.data.data || [];
      const invoices = invoicesRes.data.data || [];

      const completed = orders.filter(o => o.status === 'completed');
      const scheduled = orders.filter(o => o.status === 'scheduled');
      const pending = orders.filter(o => o.status === 'pending_review');
      const inProgress = orders.filter(o => o.status === 'in_progress');
      const totalActive = completed.length + scheduled.length;
      const completionRate = totalActive > 0 ? Math.round((completed.length / totalActive) * 100) : 0;

      const todayWOs = orders.filter(o => o.scheduled_date === todayStr && o.status !== 'cancelled');
      const upcoming = orders.filter(o => o.scheduled_date >= todayStr && o.status === 'scheduled').sort((a,b) => a.scheduled_date.localeCompare(b.scheduled_date)).slice(0, 8);
      const recent = completed.sort((a,b) => (b.completed_at||b.scheduled_date).localeCompare(a.completed_at||a.scheduled_date)).slice(0, 8);

      const overdueInvoices = invoices.filter(i => i.status === 'overdue');
      const pendingInvoices = invoices.filter(i => i.status === 'sent');
      const pendingAmt = pendingInvoices.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);

      const last30 = new Date(today); last30.setDate(today.getDate() - 30);
      const last30Str = last30.toISOString().split('T')[0];
      const facilitiesWithRecent = new Set(orders.filter(o => o.scheduled_date >= last30Str && o.status === 'completed').map(o => o.facility_id));
      const noServiceCount = facilities.filter(f => !facilitiesWithRecent.has(f.id)).length;

      setStats({ clients: clients.length, facilities: facilities.length, techCount: techs.length, completedOrders: completed.length, scheduledOrders: scheduled.length, pendingOrders: pending.length, inProgressOrders: inProgress.length, completionRate, overdueInvoices: overdueInvoices.length, pendingInvoicesAmt: pendingAmt });
      setTodayOrders(todayWOs);
      setUpcomingOrders(upcoming);
      setRecentOrders(recent);
      setExpiringLicenses(licenses);
      setFacilitiesNoService(noServiceCount);
      const compLicRes = await api.get('/companies/licenses').catch(() => ({ data: { data: [] } }));
      setCompanyLicenses(compLicRes.data.data || []);
      const timingRes = await api.get('/service-orders/timing-stats').catch(() => ({ data: { data: null } }));
      setTimingStats(timingRes.data.data || null);
    } catch (e) { console.error('Dashboard error:', e); }
    finally { setLoading(false); }
  };

  const S = {
    card: { backgroundColor:'#FFF', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)' },
    statCard: (color) => ({ backgroundColor:'#FFF', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', borderLeft:`4px solid ${color}`, cursor:'pointer', transition:'transform 0.15s' }),
    sectionTitle: { color:'#2D5C3F', margin:'0 0 14px', fontSize:'15px', fontWeight:'700' },
    woRow: { padding:'10px 12px', border:'1px solid #E8F5E9', borderRadius:'8px', fontSize:'13px', marginBottom:'6px' },
    alertRow: { padding:'10px 12px', borderRadius:'8px', fontSize:'13px', marginBottom:'6px', display:'flex', justifyContent:'space-between', alignItems:'center' },
  };

  const statusBadge = (s) => {
    const map = { scheduled:'#2196F3', in_progress:'#FF9800', completed:'#4CAF50', cancelled:'#F44336', pending_review:'#9C27B0' };
    return <span style={{ backgroundColor:map[s]||'#9E9E9E', color:'#FFF', padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'600', textTransform:'uppercase' }}>{(s||'').replace('_',' ')}</span>;
  };

  const urgencyColor = { expired:'#DC2626', critical:'#DC2626', warning:'#D97706', notice:'#2563EB' };
  const urgencyBg = { expired:'#FEE2E2', critical:'#FEE2E2', warning:'#FEF3C7', notice:'#DBEAFE' };
  const urgencyLabel = (days) => days < 0 ? 'EXPIRED' : `${days}d left`;

  return (
    <Layout>
      {showWarning && recurringStatus && <RecurringWarningModal status={recurringStatus} onGenerate={() => setShowWarning(false)} onDismiss={() => setShowWarning(false)} />}
      {recurringStatus && recurringStatus.mustGenerate && <RecurringBlockingModal status={recurringStatus} onGenerated={() => checkRecurringStatus()} />}
      <div style={{ maxWidth:'1200px', margin:'0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom:'24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ color:'#2D5C3F', margin:'0 0 4px', fontSize:'26px' }}>Operations Dashboard</h2>
            <p style={{ color:'#888', margin:0, fontSize:'13px' }}>{new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</p>
          </div>
          <button onClick={loadDashboard} style={{ backgroundColor:'#F0F7F4', color:'#2D5C3F', border:'1px solid #C8E6C9', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>🔄 Refresh</button>
        </div>

        {loading ? <div style={{ textAlign:'center', padding:'60px', color:'#888' }}>Loading dashboard...</div> : (<>

          {/* Alert Banner */}
          {(expiringLicenses.filter(l => l.urgency !== 'notice').length > 0 || stats.overdueInvoices > 0) && (
            <div style={{ backgroundColor:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:'10px', padding:'12px 16px', marginBottom:'20px', display:'flex', gap:'16px', flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontWeight:'700', color:'#92400E' }}>⚠️ Attention Required:</span>
              {expiringLicenses.filter(l => l.urgency==='expired'||l.urgency==='critical').length > 0 && (
                <span style={{ color:'#DC2626', fontSize:'13px', fontWeight:'600' }}>🔴 {expiringLicenses.filter(l => l.urgency==='expired'||l.urgency==='critical').length} license(s) expiring/expired ≤30 days</span>
              )}
              {expiringLicenses.filter(l => l.urgency==='warning').length > 0 && (
                <span style={{ color:'#D97706', fontSize:'13px', fontWeight:'600' }}>🟡 {expiringLicenses.filter(l => l.urgency==='warning').length} license(s) expiring in 31–60 days</span>
              )}
              {stats.overdueInvoices > 0 && (
                <span style={{ color:'#DC2626', fontSize:'13px', fontWeight:'600', cursor:'pointer' }} onClick={() => navigate('/invoicing')}>💰 {stats.overdueInvoices} overdue invoice(s)</span>
              )}
              <button onClick={() => navigate('/technicians')} style={{ marginLeft:'auto', backgroundColor:'#92400E', color:'#FFF', border:'none', padding:'6px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>View Licenses →</button>
            </div>
          )}

          {/* ── LICENSE PANEL: Technician (left) + Company (right) ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'24px' }}>

            {/* Technician Licenses — fixed height scroll */}
            <div style={{ ...S.card, display:'flex', flexDirection:'column' }}>
              <h3 style={{ ...S.sectionTitle, marginBottom:'10px' }}>🪪 Technician License Alerts</h3>
              <div style={{ overflowY:'auto', height:'200px', paddingRight:'4px' }}>
                {expiringLicenses.filter(l => l.license_owner === 'technician').length === 0
                  ? <p style={{ color:'#4CAF50', textAlign:'center', padding:'24px 0', fontSize:'13px', fontWeight:'600' }}>✅ All technician licenses current</p>
                  : expiringLicenses.filter(l => l.license_owner === 'technician').map((lic,i) => (
                    <div key={i} style={{ ...S.alertRow, backgroundColor:urgencyBg[lic.urgency], marginBottom:'6px' }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:'700', color:urgencyColor[lic.urgency], fontSize:'12px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {lic.first_name} {lic.last_name} — {lic.state}
                        </div>
                        <div style={{ color:'#555', fontSize:'11px' }}>{lic.license_type} #{lic.license_number}</div>
                        <div style={{ color:'#888', fontSize:'10px' }}>Exp: {new Date(lic.expiry_date+'T00:00:00').toLocaleDateString()}</div>
                      </div>
                      <span style={{ backgroundColor:urgencyColor[lic.urgency], color:'#FFF', padding:'3px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap', marginLeft:'8px' }}>{urgencyLabel(lic.days_until_expiry)}</span>
                    </div>
                  ))
                }
              </div>
              <button onClick={() => navigate('/technicians')} style={{ marginTop:'10px', backgroundColor:'#F0F7F4', color:'#2D5C3F', border:'1px solid #C8E6C9', padding:'7px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600', width:'100%' }}>Manage Technician Licenses →</button>
            </div>

            {/* Company Licenses — always visible */}
            <div style={{ ...S.card, display:'flex', flexDirection:'column' }}>
              <h3 style={{ ...S.sectionTitle, marginBottom:'10px' }}>🏢 Company Licenses</h3>
              <div style={{ overflowY:'auto', height:'200px', paddingRight:'4px' }}>
                {companyLicenses.length === 0
                  ? <p style={{ color:'#999', textAlign:'center', padding:'24px 0', fontSize:'13px' }}>No company licenses found</p>
                  : companyLicenses.map((lic, i) => {
                    const d = lic.expiry_date ? Math.floor((new Date(lic.expiry_date+'T00:00:00') - new Date()) / (1000*60*60*24)) : null;
                    const licColor = d === null ? '#6B7280' : d < 0 ? '#FFF' : d <= 7 ? '#FFF' : d <= 30 ? '#DC2626' : d <= 60 ? '#D97706' : d <= 90 ? '#1D4ED8' : '#2E7D32';
                    const licBg = d === null ? '#F9FAFB' : d < 0 ? '#7F1D1D' : d <= 7 ? '#DC2626' : d <= 30 ? '#FEE2E2' : d <= 60 ? '#FEF3C7' : d <= 90 ? '#DBEAFE' : '#E8F5E9';
                    const licLabel = d === null ? 'No expiry' : d < 0 ? 'EXPIRED' : `${d}d left`;
                    return (
                      <div key={i} style={{ backgroundColor:licBg, borderRadius:'10px', padding:'12px 14px', marginBottom:'8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{ fontWeight:'700', color:licColor, fontSize:'13px' }}>{lic.state} — {lic.license_type}</div>
                          <div style={{ fontSize:'11px', color:licColor, opacity:0.85 }}>#{lic.license_number}{lic.issued_by ? ` • ${lic.issued_by}` : ''}</div>
                          <div style={{ fontSize:'10px', color:licColor, opacity:0.75, marginTop:'2px' }}>
                            {lic.expiry_date ? `Expires: ${new Date(lic.expiry_date+'T00:00:00').toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'})}` : 'No expiry date'}
                          </div>
                        </div>
                        <span style={{ backgroundColor:'rgba(0,0,0,0.15)', color:licColor, padding:'4px 10px', borderRadius:'10px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap', marginLeft:'10px' }}>{licLabel}</span>
                      </div>
                    );
                  })
                }
              </div>
              <button onClick={() => window.location.href='/settings'} style={{ marginTop:'10px', backgroundColor:'#F0F7F4', color:'#2D5C3F', border:'1px solid #C8E6C9', padding:'7px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600', width:'100%' }}>Manage Company Licenses →</button>
            </div>
          </div>

          {/* KPI Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(148px, 1fr))', gap:'14px', marginBottom:'24px' }}>
            {[
              { val:stats.clients, label:'Active Clients', color:'#2D5C3F', to:'/clients' },
              { val:stats.facilities, label:'Facilities', color:'#D4AF37', to:'/clients' },
              { val:stats.techCount, label:'Technicians', color:'#2196F3', to:'/technicians' },
              { val:stats.inProgressOrders, label:'In Progress', color:'#FF9800' },
              { val:stats.scheduledOrders, label:'Scheduled WOs', color:'#4CAF50' },
              { val:stats.completedOrders, label:'Completed WOs', color:'#2D5C3F' },
              { val:`${stats.completionRate}%`, label:'Completion Rate', color:stats.completionRate>=80?'#4CAF50':stats.completionRate>=60?'#FF9800':'#F44336' },
              { val:stats.pendingOrders, label:'Pending Review', color:'#9C27B0' },
            ].map((s,i) => (
              <div key={i} style={S.statCard(s.color)} onClick={() => s.to && navigate(s.to)}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform='none'}>
                <div style={{ fontSize:'28px', fontWeight:'700', color:s.color }}>{s.val}</div>
                <div style={{ fontSize:'12px', color:'#888', fontWeight:'500', marginTop:'2px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Today's Schedule */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
            <div style={S.card}>
              <h3 style={S.sectionTitle}>📅 Today's Schedule ({todayOrders.length})</h3>
              {todayOrders.length === 0
                ? <p style={{ color:'#999', textAlign:'center', padding:'24px 0', fontSize:'13px' }}>No visits scheduled for today</p>
                : todayOrders.map(o => (
                  <div key={o.id} style={S.woRow}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2px' }}>
                      <span style={{ fontWeight:'700', color:'#2D5C3F', fontSize:'13px' }}>{o.order_number}</span>
                      {statusBadge(o.status)}
                    </div>
                    <div style={{ color:'#555', fontSize:'12px' }}>{o.facility_name||'—'}</div>
                    <div style={{ color:'#888', fontSize:'11px', marginTop:'2px' }}>
                      {o.scheduled_time_start ? `🕐 ${o.scheduled_time_start.substring(0,5)}` : ''}{o.tech_first_name ? ` • 👤 ${o.tech_first_name} ${o.tech_last_name}` : ''}
                    </div>
                  </div>
                ))
              }
            </div>
            <div style={S.card}>
              <h3 style={S.sectionTitle}>📅 Upcoming Visits</h3>
              {upcomingOrders.length === 0
                ? <p style={{ color:'#999', textAlign:'center', padding:'24px 0', fontSize:'13px' }}>No upcoming visits</p>
                : upcomingOrders.map(o => (
                  <div key={o.id} style={S.woRow}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2px' }}>
                      <span style={{ fontWeight:'600', color:'#2D5C3F', fontSize:'13px' }}>{o.order_number}</span>
                      <span style={{ fontSize:'11px', color:'#888' }}>📅 {new Date(o.scheduled_date+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
                    </div>
                    <div style={{ color:'#555', fontSize:'12px' }}>{o.facility_name||'—'}</div>
                    {o.tech_first_name && <div style={{ color:'#888', fontSize:'11px' }}>👤 {o.tech_first_name} {o.tech_last_name}</div>}
                  </div>
                ))
              }
            </div>
          </div>

          {/* Recently Completed */}
          <div style={{ marginBottom:'20px' }}>
            <div style={S.card}>
              <h3 style={S.sectionTitle}>✅ Recently Completed</h3>
              {recentOrders.length === 0
                ? <p style={{ color:'#999', textAlign:'center', padding:'24px 0', fontSize:'13px' }}>No completed visits yet</p>
                : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:'8px' }}>
                  {recentOrders.map(o => (
                    <div key={o.id} style={S.woRow}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2px' }}>
                        <span style={{ fontWeight:'600', color:'#333', fontSize:'13px' }}>{o.order_number}</span>
                        {statusBadge(o.status)}
                      </div>
                      <div style={{ color:'#555', fontSize:'12px' }}>{o.facility_name||'—'}</div>
                      {o.tech_first_name && <div style={{ color:'#888', fontSize:'11px' }}>👤 {o.tech_first_name} {o.tech_last_name}</div>}
                    </div>
                  ))}
                  </div>
              }
            </div>
          </div>

          {/* Operational Insights */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'16px', marginBottom:'20px' }}>
            <div style={{ ...S.card, borderLeft:'4px solid #4CAF50' }}>
              <div style={{ fontSize:'28px', fontWeight:'700', color:'#4CAF50' }}>{stats.completionRate}%</div>
              <div style={{ fontSize:'13px', color:'#555', fontWeight:'600' }}>WO Completion Rate</div>
              <div style={{ fontSize:'11px', color:'#888', marginTop:'4px' }}>{stats.completedOrders} completed / {stats.completedOrders + stats.scheduledOrders} total active</div>
            </div>
            <div style={{ ...S.card, borderLeft:`4px solid ${stats.overdueInvoices>0?'#F44336':'#4CAF50'}`, cursor:'pointer' }} onClick={() => navigate('/invoicing')}>
              <div style={{ fontSize:'28px', fontWeight:'700', color:stats.overdueInvoices>0?'#F44336':'#4CAF50' }}>{stats.overdueInvoices}</div>
              <div style={{ fontSize:'13px', color:'#555', fontWeight:'600' }}>Overdue Invoices</div>
              <div style={{ fontSize:'11px', color:'#888', marginTop:'4px' }}>${stats.pendingInvoicesAmt.toFixed(2)} pending collection</div>
            </div>
            <div style={{ ...S.card, borderLeft:`4px solid ${facilitiesNoService>0?'#FF9800':'#4CAF50'}` }}>
              <div style={{ fontSize:'28px', fontWeight:'700', color:facilitiesNoService>0?'#FF9800':'#4CAF50' }}>{facilitiesNoService}</div>
              <div style={{ fontSize:'13px', color:'#555', fontWeight:'600' }}>Facilities w/o Service</div>
              <div style={{ fontSize:'11px', color:'#888', marginTop:'4px' }}>No completed visit in last 30 days</div>
            </div>
          </div>


          {/* ── TIMING & PERFORMANCE CARD ── */}
          {timingStats && timingStats.overall && (() => {
            const o = timingStats.overall;
            const m = timingStats.month || { avg_actual_min: null, with_timing: 0, completed: 0 };
            const hasData = o.with_timing > 0;
            const fmtMin = (min) => {
              if (min === null || min === undefined) return '—';
              const h = Math.floor(min / 60);
              const m = Math.round(min % 60);
              return h > 0 ? `${h}h ${m}m` : `${m}m`;
            };
            const varianceColor = (pct) => {
              if (pct === null) return '#888';
              if (Math.abs(pct) <= 10) return '#4CAF50';
              if (Math.abs(pct) <= 20) return '#FF9800';
              return '#F44336';
            };
            const varianceBg = (pct) => {
              if (pct === null) return '#F9FAFB';
              if (Math.abs(pct) <= 10) return '#E8F5E9';
              if (Math.abs(pct) <= 20) return '#FFF3E0';
              return '#FFEBEE';
            };
            return (
              <div style={{ ...S.card, marginBottom:'20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                  <h3 style={{ ...S.sectionTitle, margin:0 }}>⏱️ Service Timing & Performance</h3>
                  {!hasData && <span style={{ fontSize:'12px', color:'#888', backgroundColor:'#F3F4F6', padding:'4px 10px', borderRadius:'10px' }}>📡 Collecting data — available after first timed service</span>}
                </div>

                {/* Top KPI row */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:'12px', marginBottom:'16px' }}>
                  {[
                    { label:'Avg Actual Time', val: fmtMin(o.avg_actual_min), color:'#2D5C3F', sub:'all time' },
                    { label:'Avg Estimated', val: fmtMin(o.avg_estimated_min), color:'#2196F3', sub:'scheduled window' },
                    { label:'Time Variance', val: o.variance_pct !== null ? `${o.variance_pct > 0 ? '+' : ''}${o.variance_pct}%` : '—', color: varianceColor(o.variance_pct), sub: o.variance_pct > 0 ? 'running over' : o.variance_pct < 0 ? 'running under' : 'on target' },
                    { label:'This Month Avg', val: fmtMin(m.avg_actual_min), color:'#9C27B0', sub:`${m.with_timing}/${m.completed} timed` },
                    { label:'High Variance WOs', val: o.high_variance_count, color: o.high_variance_count > 0 ? '#F44336' : '#4CAF50', sub:'>20% off estimate' },
                    { label:'Currently Active', val: o.in_progress, color:'#FF9800', sub:'in progress now' },
                  ].map((s,i) => (
                    <div key={i} style={{ backgroundColor:'#F9FAFB', borderRadius:'10px', padding:'12px', borderLeft:`3px solid ${s.color}` }}>
                      <div style={{ fontSize:'22px', fontWeight:'700', color:s.color }}>{s.val}</div>
                      <div style={{ fontSize:'11px', fontWeight:'600', color:'#555', marginTop:'2px' }}>{s.label}</div>
                      <div style={{ fontSize:'10px', color:'#999', marginTop:'1px' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>

                  {/* Per-technician table */}
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:'#2D5C3F', marginBottom:'8px' }}>👷 Performance by Technician</div>
                    {timingStats.technicians.length === 0
                      ? <p style={{ color:'#999', fontSize:'12px' }}>No data yet</p>
                      : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                          <thead>
                            <tr style={{ backgroundColor:'#F3F4F6' }}>
                              <th style={{ padding:'6px 8px', textAlign:'left', color:'#555', fontWeight:'600' }}>Technician</th>
                              <th style={{ padding:'6px 8px', textAlign:'center', color:'#555', fontWeight:'600' }}>WOs</th>
                              <th style={{ padding:'6px 8px', textAlign:'center', color:'#555', fontWeight:'600' }}>Avg Real</th>
                              <th style={{ padding:'6px 8px', textAlign:'center', color:'#555', fontWeight:'600' }}>Avg Est</th>
                              <th style={{ padding:'6px 8px', textAlign:'center', color:'#555', fontWeight:'600' }}>Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timingStats.technicians.map((t,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #F0F0F0' }}>
                                <td style={{ padding:'6px 8px', fontWeight:'600', color:'#333' }}>{t.name}</td>
                                <td style={{ padding:'6px 8px', textAlign:'center', color:'#666' }}>{t.completed}</td>
                                <td style={{ padding:'6px 8px', textAlign:'center', color:'#2D5C3F', fontWeight:'600' }}>{fmtMin(t.avg_actual_min)}</td>
                                <td style={{ padding:'6px 8px', textAlign:'center', color:'#2196F3' }}>{fmtMin(t.avg_estimated_min)}</td>
                                <td style={{ padding:'6px 8px', textAlign:'center' }}>
                                  {t.variance_pct !== null
                                    ? <span style={{ backgroundColor: varianceBg(t.variance_pct), color: varianceColor(t.variance_pct), padding:'2px 6px', borderRadius:'8px', fontWeight:'700', fontSize:'11px' }}>
                                        {t.variance_pct > 0 ? '+' : ''}{t.variance_pct}%
                                      </span>
                                    : <span style={{ color:'#ccc' }}>—</span>
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    }
                  </div>

                  {/* High variance WOs */}
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:'#F44336', marginBottom:'8px' }}>⚠️ High Variance Work Orders</div>
                    {timingStats.high_variance_wos.length === 0
                      ? <p style={{ color:'#4CAF50', fontSize:'12px', fontWeight:'600' }}>✅ No high-variance WOs — all within 20%</p>
                      : timingStats.high_variance_wos.map((wo,i) => (
                          <div key={i} style={{ backgroundColor: varianceBg(wo.variance_pct), borderRadius:'8px', padding:'8px 10px', marginBottom:'6px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                              <span style={{ fontWeight:'700', color:'#333', fontSize:'12px' }}>{wo.order_number}</span>
                              <span style={{ backgroundColor: varianceColor(wo.variance_pct), color:'#FFF', padding:'2px 8px', borderRadius:'8px', fontSize:'11px', fontWeight:'700' }}>
                                {wo.variance_pct > 0 ? '+' : ''}{wo.variance_pct}%
                              </span>
                            </div>
                            <div style={{ fontSize:'11px', color:'#555', marginTop:'2px' }}>{wo.facility_name}</div>
                            <div style={{ fontSize:'10px', color:'#888', marginTop:'1px' }}>
                              👤 {wo.tech_name} • Real: {fmtMin(wo.actual_min)} vs Est: {fmtMin(wo.estimated_min)}
                            </div>
                          </div>
                        ))
                    }
                  </div>
                </div>

                {!hasData && (
                  <div style={{ marginTop:'12px', backgroundColor:'#EFF6FF', borderRadius:'8px', padding:'10px 14px', fontSize:'12px', color:'#1D4ED8' }}>
                    💡 <strong>Tip:</strong> Timing data is captured automatically when technicians start and complete work orders via the mobile app. Data will populate here after the first completed service.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Quick Actions */}
          <div style={S.card}>
            <h3 style={S.sectionTitle}>⚡ Quick Actions</h3>
            <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
              {[
                { label:'👥 Clients', to:'/clients', bg:'#2D5C3F' },
                { label:'📅 Schedule', to:'/schedule', bg:'#2196F3' },
                { label:'🔧 Technicians', to:'/technicians', bg:'#FF9800' },
                { label:'💰 Invoicing', to:'/invoicing', bg:stats.overdueInvoices>0?'#F44336':'#9C27B0' },
                { label:'📋 Proposals', to:'/proposals', bg:'#607D8B' },
                { label:'⚙️ Settings', to:'/settings', bg:'#455A64' },
                { label:'🔧 System', to:'/system-maintenance', bg:'#37474F' },
              ].map((a,i) => (
                <button key={i} onClick={() => navigate(a.to)} style={{ backgroundColor:a.bg, color:'#FFF', border:'none', padding:'10px 20px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>{a.label}</button>
              ))}
            </div>
          </div>

        </>)}
      </div>
    </Layout>
  );
};
export default Dashboard;
